import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Box, CircularProgress, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import BaseWidget from './base';
import { DashboardWidget, MapWidgetConfig, DataSourceItem } from '../../../types/dashboard.ui';
import { dashboardAPI } from '../../../api/dashboard';

// Fix for default markers in React-Leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapWidgetProps {
  widget: DashboardWidget;
  onEdit?: (widget: DashboardWidget) => void;
  onDelete?: (widget: DashboardWidget) => void;
}

interface ParamValue {
  paramId: string;
  paramName: string;
  deviceName: string;
  objectName?: string;
  value: number | string;
  unit?: string;
  timestamp: string;
  status: string;
  location?: {
    lat: number;
    lng: number;
  };
}

const MapWidget: React.FC<MapWidgetProps> = ({ widget, onEdit, onDelete }) => {
  const translate = useTranslate();
  const [paramValues, setParamValues] = useState<ParamValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const config = useMemo(() => 
    (typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config) as MapWidgetConfig || {}, 
    [widget.config]
  );

  // Extract data sources from new format
  const dataSources = useMemo<DataSourceItem[]>(() => {
    if (config.dataSource?.items && Array.isArray(config.dataSource.items)) {
      return config.dataSource.items;
    }
    return [];
  }, [config.dataSource]);

  const loadParamData = useCallback(async () => {
    if (dataSources.length === 0) {
      setParamValues([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paramIds = dataSources.map(ds => ds.paramId);
      const endTime = new Date();
      const startTime = new Date(endTime.getTime() - 60 * 1000); // Last minute

      const response = await dashboardAPI.getHistoricalDataByParams(
        paramIds,
        startTime.toISOString(),
        endTime.toISOString()
      );

      const values: ParamValue[] = [];
      
      response.forEach((series: any) => {
        const dataSource = dataSources.find(ds => ds.paramId === series.param_id);
        if (!dataSource) return;

        const latestValue = series.values.length > 0 
          ? series.values[series.values.length - 1]
          : null;

        if (latestValue) {
          // Generate demo location based on parameter for visualization
          const centerLat = config.center?.lat ?? 55.7558;
          const centerLng = config.center?.lng ?? 37.6176;
          
          values.push({
            paramId: series.param_id,
            paramName: dataSource.paramName || 'N/A',
            deviceName: dataSource.deviceName || 'N/A',
            objectName: dataSource.deviceObjectName,
            value: latestValue.value,
            unit: dataSource.paramUnit,
            timestamp: latestValue.timestamp,
            status: latestValue.status || 'normal',
            location: {
              lat: centerLat + (Math.random() - 0.5) * 0.05,
              lng: centerLng + (Math.random() - 0.5) * 0.05
            }
          });
        }
      });

      setParamValues(values);
    } catch (err) {
      console.error('Error loading parameter data:', err);
      setError(translate('resources.dashboards.widgets.map.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [dataSources, config.center, translate]);

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'active':
      case 'normal':
        return '#4caf50'; // green
      case 'warning':
        return '#ff9800'; // orange
      case 'error':
      case 'critical':
        return '#f44336'; // red
      case 'offline':
      case 'inactive':
        return '#9e9e9e'; // gray
      default:
        return '#2196f3'; // blue
    }
  };

  const createCustomIcon = (status: string): Icon => {
    const color = getStatusColor(status);
    const svgIcon = `
      <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z" 
              fill="${color}" stroke="#fff" stroke-width="2"/>
        <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
      </svg>
    `;
    
    return new Icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(svgIcon),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34]
    });
  };

  const formatValue = (value: number | string, unit?: string): string => {
    if (typeof value === 'number') {
      return `${value.toFixed(2)}${unit ? ' ' + unit : ''}`;
    }
    return `${value}${unit ? ' ' + unit : ''}`;
  };

  useEffect(() => {
    loadParamData();
    
    // Set up auto refresh (30 seconds)
    const interval = setInterval(loadParamData, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [loadParamData]);

  const content = (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : dataSources.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="textSecondary">
            {translate('resources.dashboards.widgets.map.no_data_source')}
          </Typography>
        </Box>
      ) : (
        <MapContainer
          center={[config.center?.lat ?? 55.7558, config.center?.lng ?? 37.6176]}
          zoom={config.zoom ?? 10}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {paramValues.map((param) => {
            if (!param.location) return null;
            
            return (
              <Marker
                key={param.paramId}
                position={[param.location.lat, param.location.lng]}
                icon={createCustomIcon(param.status)}
              >
                <Popup>
                  <Box sx={{ p: 1, minWidth: 200 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {param.paramName}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.875rem' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {translate('resources.dashboards.widgets.map.device')}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {param.deviceName}
                        </Typography>
                      </Box>
                      
                      {param.objectName && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">
                            {translate('resources.dashboards.widgets.map.object')}:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {param.objectName}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {translate('resources.dashboards.widgets.map.value')}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatValue(param.value, param.unit)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {translate('resources.dashboards.widgets.map.status')}:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          sx={{ color: getStatusColor(param.status) }}
                        >
                          {param.status}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {translate('resources.dashboards.widgets.map.position')}:
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace">
                          {param.location.lat.toFixed(6)}, {param.location.lng.toFixed(6)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="textSecondary">
                          {translate('resources.dashboards.widgets.map.updated')}:
                        </Typography>
                        <Typography variant="caption">
                          {new Date(param.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      )}
    </Box>
  );

  return (
    <BaseWidget
      widget={widget}
      onEdit={onEdit ? () => onEdit(widget) : undefined}
      onDelete={onDelete ? () => onDelete(widget) : undefined}
      onRefresh={loadParamData}
      loading={loading}
      error={error}
    >
      {content}
    </BaseWidget>
  );
};

export default MapWidget;
